import { App } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type File from '../../interfaces/File';
import type { FolderContentStats } from '../../interfaces/FileApiService';
import { isLockedPasswordFolder } from '../../lib/fileActions';
import { FileService } from '../../services/fileApiService';
import { isFolderContentStatsStale } from '../../store/folderContentStatsSlice';
import { useAppStore } from '../../store/store';

// Minimum delay (ms) between two folder-stats requests, to rate-limit the (potentially heavy)
// recursive aggregation endpoint and avoid hammering it on repeated clicks.
export const FOLDER_STATS_COOLDOWN_MS = 10000;

const fileService = new FileService();

function readCachedFolderStats(
  file: File,
  getFilePasswordSession: (fileId: number) => string | null | undefined,
  getFolderContentStatsFromCache: (fileId: number) => { stats: FolderContentStats } | null,
): FolderContentStats | null {
  if (!file.isFolder || isLockedPasswordFolder(file, getFilePasswordSession)) {
    return null;
  }
  return getFolderContentStatsFromCache(file.id)?.stats ?? null;
}

/**
 * On-demand folder content stats (file count, subfolder count, total size) with rate limiting.
 * Hydrates from Zustand-persisted cache on mount; never auto-fetches from API without an explicit
 * `loadFolderStats` call.
 */
export function useFolderContentStats(file: File) {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const getFilePasswordSession = useAppStore((state) => state.getFilePasswordSession);
  const getFolderContentStatsFromCache = useAppStore(
    (state) => state.getFolderContentStatsFromCache,
  );
  const setFolderContentStatsInCache = useAppStore((state) => state.setFolderContentStatsInCache);

  const [folderStats, setFolderStats] = useState<FolderContentStats | null>(() =>
    readCachedFolderStats(file, getFilePasswordSession, getFolderContentStatsFromCache),
  );
  const [isFolderStatsStale, setIsFolderStatsStale] = useState(() => {
    const cached = getFolderContentStatsFromCache(file.id);
    return cached ? isFolderContentStatsStale(cached.fetchedAt) : false;
  });
  const [isLoadingFolderStats, setIsLoadingFolderStats] = useState(false);
  const [canRefreshFolderStats, setCanRefreshFolderStats] = useState(true);

  const folderStatsCooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadFolderStats = useCallback(async () => {
    if (
      !file?.isFolder ||
      isLoadingFolderStats ||
      !canRefreshFolderStats ||
      isLockedPasswordFolder(file, getFilePasswordSession)
    ) {
      return;
    }

    setIsLoadingFolderStats(true);
    setCanRefreshFolderStats(false);

    try {
      const stats = await fileService.getFolderContentStats(file.cufi);
      setFolderContentStatsInCache(file.id, stats);
      setFolderStats(stats);
      setIsFolderStatsStale(false);
    } catch {
      notification.error({
        title: t('errors.hitASnag'),
      });
    } finally {
      setIsLoadingFolderStats(false);
      if (folderStatsCooldownRef.current) {
        clearTimeout(folderStatsCooldownRef.current);
      }
      folderStatsCooldownRef.current = setTimeout(() => {
        setCanRefreshFolderStats(true);
      }, FOLDER_STATS_COOLDOWN_MS);
    }
  }, [
    file,
    isLoadingFolderStats,
    canRefreshFolderStats,
    getFilePasswordSession,
    setFolderContentStatsInCache,
    notification,
    t,
  ]);

  useEffect(() => {
    const cached = getFolderContentStatsFromCache(file.id);
    const stats = readCachedFolderStats(
      file,
      getFilePasswordSession,
      getFolderContentStatsFromCache,
    );
    setFolderStats(stats);
    setIsFolderStatsStale(cached ? isFolderContentStatsStale(cached.fetchedAt) : false);
    setIsLoadingFolderStats(false);
    setCanRefreshFolderStats(true);
    if (folderStatsCooldownRef.current) {
      clearTimeout(folderStatsCooldownRef.current);
      folderStatsCooldownRef.current = null;
    }
  }, [file, getFilePasswordSession, getFolderContentStatsFromCache]);

  useEffect(() => {
    return () => {
      if (folderStatsCooldownRef.current) {
        clearTimeout(folderStatsCooldownRef.current);
      }
    };
  }, []);

  return {
    canRefreshFolderStats,
    folderStats,
    isFolderStatsStale,
    isLoadingFolderStats,
    loadFolderStats,
  };
}
